import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// FETCH ROLES
export const fetchAllRoles = createAsyncThunk(
  "roles/getAll",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/roles", { params });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch roles"
      );
    }
  }
);

// CREATE ROLE
export const addRole = createAsyncThunk(
  "roles/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/roles", payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to create role"
      );
    }
  }
);

// FETCH SINGLE ROLE
export const getRoleById = createAsyncThunk(
  "roles/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/roles/${id}`);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch role"
      );
    }
  }
);

// UPDATE ROLE
export const updateRole = createAsyncThunk(
  "roles/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/roles/${id}`, payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to update role"
      );
    }
  }
);

// DELETE ROLE BY ID
export const deleteRole = createAsyncThunk(
  "roles/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/roles/${id}`);

      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete role");
    }
  }
);

// CHANGE ROLE STATUS
export const changeRoleStatus = createAsyncThunk(
  "roles/changeStatus",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/tenant/roles/${id}/change-status`);

      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to change role status"
      );
    }
  }
);

// BULK DELETE ROLES
export const bulkDeleteRoles = createAsyncThunk(
  "roles/bulkDeleteRoles",
  async (roleIds, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/roles/bulk-delete`, {
        data: { ids: roleIds },
      });

      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// GET PERMISSION BASED ON ROLE ID
export const rolesPermission = createAsyncThunk(
  "roles/rolesPermission",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await Axios.put(`/tenant/roles/${id}/permissions`);

      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to get role permissions");
    }
  }
);

// UPDATE PERMISSION BASED ON ROLE ID
export const updateRolePermissions = createAsyncThunk(
  "roles/updatePermission",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await Axios.put(
        `/tenant/roles/${id}/permissions`,
        payload
      );

      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

const rolesSlice = createSlice({
  name: "roles",
  initialState: {
    roles: [],
    total_records: 0,
    rolePermission: [],
    selectedRole: null,
    isLoading: false,
    error: null,
    isBulkDeleting: false,
  },

  extraReducers: (builder) => {
    builder
      //GET ALL ROLES
      .addCase(fetchAllRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchAllRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch roles";
      })

      // GET ALL ROLES PERMISSION LIST
      .addCase(rolesPermission.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rolesPermission.fulfilled, (state, action) => {
        state.isLoading = false;
        state.rolePermission = action.payload;
      })
      .addCase(rolesPermission.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      // GET SINGLE ROLE
      .addCase(getRoleById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRoleById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedRole = action.payload;
      })
      .addCase(getRoleById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch role";
      })

      // BULK DELETE ROLES
      .addCase(bulkDeleteRoles.pending, (state) => {
        state.isBulkDeleting = true;
        state.error = null;
      })
      .addCase(bulkDeleteRoles.fulfilled, (state) => {
        state.isBulkDeleting = false;
      })
      .addCase(bulkDeleteRoles.rejected, (state, action) => {
        state.isBulkDeleting = false;
        state.error = action.payload || "Failed to bulk delete roles";
      });
  },
});

export default rolesSlice.reducer;
